"use client"
import { CarModelStripDto, HomePageCarStripDto } from "api"
import useShopUrl from "common/bit/hooks/useShopUrl"
import ColorImageSwiper from "components/homepage/colorSwiper/ColorImageSwiper"
import SwiperFooterText from "components/homepage/colorSwiper/SwiperFooterText"
import SwiperTopText from "components/homepage/colorSwiper/SwiperTopText"
import React, { useEffect, useMemo, useState } from "react"
import ShopLoader from "reusable/ShopLoader"
import styled from "styled-components"
import SwiperCore from "swiper"
import { HEADER_HEIGHT_D } from "theme/shared"

// Assuming a similar model structure as seen in Mazda project, adjust if needed
// import { ModelsPageModel } from 'path/to/model/type';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`
const StyledTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  margin-top: 47px;
  text-align: center;
  font-weight: 400;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 28px;
    margin-top: 28px;
  }
`

export const StyledSwiperMain = styled.div`
  contain: paint;
  max-width: 100vw;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    justify-content: center;
    height: calc(100vh - ${HEADER_HEIGHT_D}px - 50px);
  }
`
const StyledDisclaimer = styled.div`
  font-size: 16px;
  font-weight: 300;
  text-align: center;
  line-height: 1.4;
  width: 300px;
  margin-top: 32px;

  ${(p) => p.theme.breakpoints.up("md")} {
    width: 500px;
    padding: 0;
    font-size: 18px;
    line-height: 1.5;
  }
`
const StyledOutOfStockText = styled.div`
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  line-height: 1.4;
  margin-top: 32px;
  color: ${(p) => p.theme.colors.mazdaRed};
  display: flex;
  align-items: center;
  gap: 12px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 0;
  }
`

// Define props for ShopLobby component
interface ShopLobbyProps {
  carsStrip: CarModelStripDto[]
}

const mapCarModelStrip = (car: CarModelStripDto): HomePageCarStripDto => {
  return {
    ...car,
    modelColors: [
      {
        order: 0 /* int32 */,
        colorHex: "#FFFFFF" /* string */,
        colorCode: "white" /* string */,
        colorImage: car.image,
        image: car.image,
      },
    ],
    // Add any other properties you need to map
  }
}

const ShopLobby: React.FC<ShopLobbyProps> = ({ carsStrip }) => {
  const [activeIndex, setActiveIndex] = useState(-1)
  const [colorIndex] = useState(0) // Default color index
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [firstSwiper, setFirstSwiper] = useState<SwiperCore | undefined>(
    undefined
  ) // Corrected destructuring
  const [secondSwiper] = useState<SwiperCore | undefined>(undefined) // Assuming secondSwiper might need a setter too, adjust if needed
  const mappedCarsStrip = useMemo(
    () =>
      carsStrip
        .filter((car) => car.carState !== "Future")
        .map(mapCarModelStrip),
    [carsStrip]
  )

  useEffect(() => {
    if (mappedCarsStrip.length === 0) {
      if (activeIndex !== -1) {
        setActiveIndex(-1)
      }
      return
    }

    if (activeIndex < 0 || activeIndex >= mappedCarsStrip.length) {
      setActiveIndex(0)
    }
  }, [activeIndex, mappedCarsStrip?.length])

  const isLobbyReady = activeIndex >= 0 && activeIndex < mappedCarsStrip.length
  const activeCar = mappedCarsStrip?.[activeIndex]
  const link = useShopUrl(activeCar?.id ?? 0)
  const carLink = activeCar?.isInShop ? link : ""
  return (
    <StyledContainer>
      {!isLobbyReady && <ShopLoader />}
      <StyledTitle>כאן מתחילה הדרך לרכב החדש שלך</StyledTitle>

      <StyledSwiperMain>
        {activeCar?.isInShop ? (
          <StyledDisclaimer>
            להבטחת ההזמנה יש לשלם מקדמה בלבד.
            <br />
            לאחר מכן, ניצור איתך קשר לאישור ההזמנה ונמשיך בתהליך הרכישה
          </StyledDisclaimer>
        ) : (
          <StyledOutOfStockText>
            <img
              src="/icons/out-of-stock-icon.svg"
              alt="לא במלאי"
              width="24"
              height="24"
            />
            לא במלאי באתר
          </StyledOutOfStockText>
        )}
        <ColorImageSwiper
          carsStrip={mappedCarsStrip}
          setActiveIndex={setActiveIndex}
          colorIndex={colorIndex}
          setControlledSwiper={setFirstSwiper}
          controlledSwiper={secondSwiper}
          isshop
        />
        {activeCar && (
          <SwiperTopText
            carData={activeCar}
            carLink={carLink}
            isshop
            isPreSale={activeCar?.carState === "PreSale"}
          />
        )}
        {activeCar && (
          <SwiperFooterText
            card={activeCar}
            activeIndex={colorIndex}
            link={carLink}
            colorIndex={colorIndex}
            isshop
          />
        )}
      </StyledSwiperMain>
    </StyledContainer>
  )
}

export default ShopLobby
